import { logger } from "../core/Logger.js";
import {
    Sound,
    Engine
} from "@babylonjs/core";

/**
 * ✅ AudioManager pour Babylon.js
 * Utilise l'API Sound standard
 */

const MUSIC_CONFIG = {
    ambient: { url: "/assets/musics/background_ambient.mp3", volume: 0.35 }
};

const SFX_URLS = {
    dash: "/assets/sfx/dash.mp3", // Update these links with better ones
    shoot: "/assets/sfx/shoot.mp3",
    explosion: "/assets/sfx/explosion.mp3",
    bonus: "/assets/sfx/bonus.mp3",
    hit: "/assets/sfx/hit.mp3",
    gunshot: "https://playground.babylonjs.com/sounds/gunshot.wav",
    violons: "https://playground.babylonjs.com/sounds/violons11.wav",
    // Using stable babylonjs playground sounds to replace broken freesound links
    lazer_shoot: "https://playground.babylonjs.com/sounds/gunshot.wav",
    lazer_hit: "https://playground.babylonjs.com/sounds/bounce.wav",
    whoosh: "https://playground.babylonjs.com/sounds/bounce.wav",
    boom: "https://playground.babylonjs.com/sounds/gunshot.wav",
    powerup: "https://playground.babylonjs.com/sounds/bounce.wav"
};

export class AudioManager {
    constructor(scene, engine) {
        logger.info("AUDIO", "DEBUG", "🔥 AudioManager créé (Babylon.js 8.x API)");

        this.scene = scene;
        this.engine = engine;
        this.audioEngine = null;

        this.musics = new Map();
        this.sfx = new Map();

        this.currentMusicKey = null;
        this.isReady = false;
        this.isUnlocked = false;
        this.masterVolume = 1.0;
    }

    /**
     * Initialise l'audio de manière NON-BLOQUANTE
     * Le jeu peut démarrer pendant que l'audio charge
     */
    async initAudio() {
        if (this.isReady) return;
        logger.info("AUDIO", "DEBUG", "⏳ Initialisation Audio...");

        try {
            // ✅ Dans Babylon.js, l'audioEngine est lié à l'engine
            this.audioEngine = this.engine.getAudioEngine ? this.engine.getAudioEngine() : Engine.audioEngine;

            if (!this.audioEngine) {
                logger.warn("AUDIO", "DEBUG", "⚠️ AudioEngine non disponible immédiatement");
            }

            // ✅ ÉTAPE 2 : Charger les musiques
            await this._loadStreamingMusics();

            // ✅ ÉTAPE 3 : Charger les SFX
            await this._loadSFX();

            this.isReady = true;
            logger.info("AUDIO", "DEBUG", "✅ Audio Manager prêt !");

        } catch (error) {
            logger.error("AUDIO", "DEBUG", "❌ Erreur lors de l'initialisation audio:", error);
        }
    }

    /**
     * Déverrouille l'audio (requis par les navigateurs)
     * Doit être appelé dans un gestionnaire d'événement utilisateur
     */
    async unlockAudio() {
        logger.info("AUDIO", "DEBUG", "🔓 unlockAudio() appelé");

        if (this.isUnlocked) {
            logger.info("AUDIO", "DEBUG", "   ✅ Déjà déverrouillé");
            return;
        }

        // On récupère l'engine au cas où il ne l'était pas au début
        if (!this.audioEngine) {
            this.audioEngine = this.engine.getAudioEngine ? this.engine.getAudioEngine() : Engine.audioEngine;
        }

        // Si toujours null, on essaie de forcer sa création via l'API statique
        if (!this.audioEngine && Engine.audioEngine) {
            this.audioEngine = Engine.audioEngine;
        }

        if (!this.audioEngine) {
            logger.error("AUDIO", "DEBUG", "❌ Moteur audio non disponible");
            // Tentative de secours : Babylon peut avoir besoin qu'on accède à Engine.audioEngine pour l'instancier
            try {
                this.audioEngine = Engine.audioEngine;
                logger.info("AUDIO", "DEBUG", "🔄 Tentative de récupération via Engine.audioEngine statique...");
            } catch(e) {
                logger.error("AUDIO", "DEBUG", "❌ Échec critique de récupération de l'AudioEngine");
            }
        }

        if (!this.audioEngine) {
            logger.error("AUDIO", "DEBUG", "❌ DEFINITIVEMENT INDISPONIBLE");
            return;
        }

        try {
            // ✅ Déverrouillage standard Babylon.js
            logger.info("AUDIO", "DEBUG", "🔓 Tentative de déverrouillage de l'audioEngine...");
            this.audioEngine.unlock();
            
            this.isUnlocked = true;
            logger.info("AUDIO", "DEBUG", "✅✅✅ AUDIO DÉVERROUILLÉ ✅✅✅");

        } catch (e) {
            logger.error("AUDIO", "DEBUG", "❌ Erreur unlockAudio:", e);
        }
    }

    /**
     * Charge les musiques en mode STREAMING
     * Avantage : pas de chargement en mémoire, lecture instantanée
     */
    async _loadStreamingMusics() {
        logger.info("AUDIO", "DEBUG", "📥 Chargement des musiques...");

        const promises = Object.entries(MUSIC_CONFIG).map(([key, config]) => {
            return new Promise((resolve) => {
                logger.info("AUDIO", "DEBUG", `   🎵 Chargement: ${key}`);
                const sound = new Sound(
                    key,
                    config.url,
                    this.scene,
                    () => {
                        logger.info("AUDIO", "DEBUG", `   ✅ Prêt: ${key}`);
                        resolve();
                    },
                    {
                        loop: true,
                        autoplay: false,
                        volume: config.volume,
                        streaming: true
                    }
                );
                this.musics.set(key, sound);
            });
        });

        await Promise.all(promises);
        logger.info("AUDIO", "DEBUG", "✅ Toutes les musiques chargées");
    }

    /**
     * Charge les SFX en buffer
     */
    async _loadSFX() {
        logger.info("AUDIO", "DEBUG", "📥 Chargement des SFX...");

        const sfxConfigs = [
            { name: "dash",      url: SFX_URLS.whoosh,      volume: 0.09, playbackRate: 1.5 },
            { name: "shoot",     url: SFX_URLS.lazer_shoot, volume: 0.05 },
            { name: "explosion", url: SFX_URLS.boom,        volume: 0.10 },
            { name: "bonus",     url: SFX_URLS.powerup,     volume: 0.10 },
            { name: "hit",       url: SFX_URLS.lazer_hit,   volume: 0.07, playbackRate: 1.2 }
        ];

        const promises = sfxConfigs.map(config => {
            return new Promise((resolve) => {
                const sound = new Sound(
                    config.name,
                    config.url,
                    this.scene,
                    () => resolve(),
                    {
                        volume: config.volume,
                        playbackRate: config.playbackRate || 1.0
                    }
                );
                this.sfx.set(config.name, sound);
            });
        });

        await Promise.all(promises);
        logger.info("AUDIO", "DEBUG", "✅ Tous les SFX chargés");
    }

    /**
     * Joue une musique de fond
     */
    playMusic(key) {
        // Force redirection to background ambient music only
        key = "ambient";
        logger.info("AUDIO", "DEBUG", `🎵 playMusic("${key}")`);

        // On s'assure d'avoir l'audioEngine
        if (!this.audioEngine) {
            this.audioEngine = this.engine.getAudioEngine ? this.engine.getAudioEngine() : Engine.audioEngine;
        }

        // Si toujours null, on essaie de forcer sa création via l'API statique
        if (!this.audioEngine && Engine.audioEngine) {
            this.audioEngine = Engine.audioEngine;
        }

        // Vérification du verrouillage
        if (this.audioEngine && !this.isUnlocked) {
            if (this.audioEngine.unlocked) {
                this.isUnlocked = true;
            } else {
                logger.error("AUDIO", "DEBUG", "❌ AUDIO NON DÉVERROUILLÉ");
                logger.error("AUDIO", "DEBUG", "   Appelez unlockAudio() dans un event handler utilisateur");
                return;
            }
        }

        if (!this.isReady) {
            logger.warn("AUDIO", "DEBUG", "⚠️ Audio pas encore prêt, tentative quand même...");
        }

        // Vérifie si déjà en lecture
        if (this.currentMusicKey === key) {
            const current = this.musics.get(key);
            if (current && current.isPlaying) {
                logger.info("AUDIO", "DEBUG", "   ⚠️ Déjà en lecture");
                return;
            }
        }

        // Stop musique précédente
        if (this.currentMusicKey) {
            const prevMusic = this.musics.get(this.currentMusicKey);
            if (prevMusic && prevMusic.isPlaying) {
                logger.info("AUDIO", "DEBUG", `   ⏹️ Stop: ${this.currentMusicKey}`);
                prevMusic.stop();
            }
        }

        // Lance nouvelle musique
        const nextMusic = this.musics.get(key);
        if (!nextMusic) {
            logger.error("AUDIO", "DEBUG", `❌ Musique introuvable: ${key}`);
            logger.info("AUDIO", "DEBUG", "   Disponibles:", Array.from(this.musics.keys()));
            return;
        }

        this.currentMusicKey = key;

        try {
            nextMusic.play();
        } catch (e) {
            logger.error("AUDIO", "DEBUG", `❌ Erreur play():`, e);
        }
    }

    /**
     * Joue un effet sonore
     */
    playSound(key) {
        if (!this.isUnlocked) {
            logger.warn("AUDIO", "DEBUG", `⚠️ SFX "${key}" ignoré (audio locked)`);
            return;
        }

        const sound = this.sfx.get(key);
        if (sound) {
            logger.info("AUDIO", "DEBUG", `🔊 SFX: ${key}`);
            sound.play();
        } else {
            logger.warn("AUDIO", "DEBUG", `⚠️ SFX introuvable: ${key}`);
        }
    }

    /**
     * Arrête toutes les musiques
     */
    stopAll() {
        logger.info("AUDIO", "DEBUG", "⏹️ Arrêt de tous les sons");

        if (this.currentMusicKey) {
            const music = this.musics.get(this.currentMusicKey);
            if (music && music.isPlaying) {
                music.stop();
            }
        }

        this.currentMusicKey = null;
    }

    /**
     * Définit le volume global
     * @param {number} volume - De 0.0 à 1.0
     */
    setMasterVolume(volume) {
        this.masterVolume = Math.max(0, Math.min(1, volume));
        if (this.audioEngine) {
            this.audioEngine.setGlobalVolume(this.masterVolume);
        } else if (Engine.audioEngine) {
            Engine.audioEngine.setGlobalVolume(this.masterVolume);
        }
    }
}